'use client';

import Image from 'next/image';
import { usePathname, useRouter } from 'next/navigation';
import { useTheme } from 'next-themes';
import * as React from 'react';
import { IconPlus } from '@tabler/icons-react';

import { NewSpaceDialog } from '@/components/spaces/new-space-dialog';
import { Coachmark } from '@/components/tour/coachmark';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { isMacPlatform } from '@/components/deck-editor/shortcuts/keymap';
import {
  COACHMARKS,
  COMMENTS_PROMPT,
  DECK_PROMPT,
  GRADUATION,
  SHEET_PROMPT,
  type CoachmarkCopy,
} from '@/lib/tour/copy';
import {
  expandTourDemoTree,
  setTourDemoTreeExpandWanted,
  isTourAnchorVisible,
  prefillTourComposer,
  revealTourChatPanel,
  sendTourComposer,
  setTourCanvasLock,
  setTourComposerLock,
  getTourExpectedDocumentId,
  setTourPendingDemoReplayId,
  subscribeTourChatEvents,
  subscribeTourDictationEnd,
  tourDeckGoToSlide,
  type TourDemoReplayId,
} from '@/lib/tour/tour-bridge';
import {
  completedStepsForStage,
  isTourStage,
  mergeCompletedSteps,
  transitionTourStage,
  type TourSignal,
  type TourStage,
} from '@/lib/tour/tour-state-reducer';
import { useDemoSpaceStatus } from '@/lib/tour/use-demo-status';
import { useTourState } from '@/lib/tour/use-tour-state';
import { track } from '@/lib/report-error';
import { useSpaces, type Space } from '@/components/spaces/use-spaces';

interface StageConfig {
  copy: CoachmarkCopy;
  /** data-tour ids, in priority order; first visible instance wins. */
  anchors: string[];
  side: 'top' | 'right' | 'bottom' | 'left';
  align: 'start' | 'center' | 'end';
  /** Render nothing until the anchor resolves (no floating fallback). */
  requireAnchor?: boolean;
  /** False renders no scrim at all — explore beats ("click through them")
   * keep the whole page interactive, with just the ring and the popover. */
  scrim?: boolean;
  /** False blocks clicks on the spotlit target (explanation-only beats). */
  interactive?: boolean;
  /** Composer prefill on entering the stage (populated, never auto-sent). */
  prefill?: string;
  /** Pending demo-replay id attached to the next send while on this stage. */
  demoReplayId?: TourDemoReplayId;
}

const STAGE_CONFIG: Partial<Record<TourStage, StageConfig>> = {
  demoSpace: {
    copy: COACHMARKS.demoSpace,
    anchors: ['demo-space'],
    side: 'right',
    align: 'start',
  },
  // Anchor on the demo space's node+subtree in the rail; mobile has no rail,
  // so fall back to the whole tree anchor.
  dataRoom: {
    copy: COACHMARKS.dataRoom,
    anchors: ['demo-space-tree', 'files-tree'],
    side: 'right',
    align: 'start',
  },
  openModel: {
    copy: COACHMARKS.openModel,
    anchors: ['demo-doc-sheet'],
    side: 'bottom',
    align: 'start',
  },
  sheetPrompt: {
    copy: COACHMARKS.sheetPrompt,
    anchors: ['chat-composer'],
    side: 'top',
    align: 'start',
    requireAnchor: true,
    prefill: SHEET_PROMPT,
    demoReplayId: 'sheet',
  },
  sheetDone: {
    copy: COACHMARKS.sheetDone,
    anchors: ['sheet-tab-strip'],
    side: 'top',
    align: 'start',
    requireAnchor: true,
  },
  backToSpace: {
    copy: COACHMARKS.backToSpace,
    anchors: ['editor-back'],
    side: 'right',
    align: 'start',
  },
  openCim: {
    copy: COACHMARKS.openCim,
    anchors: ['demo-doc-deck'],
    side: 'bottom',
    align: 'start',
    requireAnchor: true,
  },
  deckPrompt: {
    copy: COACHMARKS.deckPrompt,
    anchors: ['chat-composer'],
    side: 'top',
    align: 'start',
    requireAnchor: true,
    prefill: DECK_PROMPT,
    demoReplayId: 'deck',
  },
  deckDone: {
    copy: COACHMARKS.deckDone,
    anchors: ['filmstrip'],
    side: 'right',
    align: 'start',
    requireAnchor: true,
  },
  commentsIntro: {
    copy: COACHMARKS.commentsIntro,
    anchors: ['slide-canvas'],
    side: 'left',
    align: 'center',
    requireAnchor: true,
  },
  commentsPrompt: {
    copy: COACHMARKS.commentsPrompt,
    anchors: ['chat-composer'],
    side: 'top',
    align: 'start',
    requireAnchor: true,
    prefill: COMMENTS_PROMPT,
    demoReplayId: 'comments',
  },
  voiceIntro: {
    copy: COACHMARKS.voiceIntro,
    anchors: ['chat-composer'],
    side: 'top',
    align: 'start',
    requireAnchor: true,
  },
  chromeShare: {
    copy: COACHMARKS.chromeShare,
    anchors: ['share-button'],
    side: 'bottom',
    align: 'end',
    interactive: false,
  },
};

/** Where the back arrow lands from each beat; beats absent here show none. */
const BACK_TARGETS: Partial<Record<TourStage, TourStage>> = {
  dataRoom: 'demoSpace',
  openModel: 'dataRoom',
  sheetPrompt: 'openModel',
  backToSpace: 'sheetDone',
  openCim: 'backToSpace',
  deckPrompt: 'openCim',
  commentsIntro: 'deckDone',
  commentsPrompt: 'commentsIntro',
  // voiceIntro has no back target: the comments replay already ran.
  chromeShare: 'voiceIntro',
};

// The demo deck replay plants its two comment threads on slide 16
// (tourDeckGoToSlide takes a 0-based index into deck order).
const COMMENTS_SLIDE_INDEX = 15;

/** Replay stages and the demo replay each one runs. */
const REPLAY_STAGE_IDS: Partial<Record<TourStage, TourDemoReplayId>> = {
  sheetReplay: 'sheet',
  deckReplay: 'deck',
  commentsReplay: 'comments',
};

/** Stages whose anchor lives on the demo space's files page: stepping back
 * to one of these navigates there so the anchor can resolve. */
const SPACE_FILES_STAGES: ReadonlySet<TourStage> = new Set([
  'demoSpace',
  'dataRoom',
  'openModel',
]);

function resolveAnchor(anchorIds: string[]): HTMLElement | null {
  for (const id of anchorIds) {
    const candidates = document.querySelectorAll<HTMLElement>(
      `[data-tour="${id}"]`
    );
    for (const candidate of candidates) {
      if (isTourAnchorVisible(candidate)) return candidate;
    }
  }
  return null;
}

// How long a vanished anchor keeps its element so the coachmark holds
// position through a navigation remount instead of blinking out and back.
const ANCHOR_HOLD_MS = 600;

function useStageAnchor(config: StageConfig | undefined): HTMLElement | null {
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
  const lastSeenAtRef = React.useRef(0);
  const anchorIds = config?.anchors;

  React.useEffect(() => {
    if (!anchorIds) {
      setAnchor(null);
      return;
    }
    const scan = () => {
      const next = resolveAnchor(anchorIds);
      if (next) {
        lastSeenAtRef.current = Date.now();
        setAnchor((current) => (current === next ? current : next));
        return;
      }
      // Miss: hold the previous element briefly — navigation swaps the DOM
      // for a few frames and dropping to null makes the coachmark blink.
      if (Date.now() - lastSeenAtRef.current > ANCHOR_HOLD_MS) {
        setAnchor(null);
      }
    };
    scan();
    const interval = window.setInterval(scan, 150);
    return () => window.clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorIds?.join(',')]);

  return anchor;
}

const PREFILL_MAX_ATTEMPTS = 30;
const FALLBACK_GRACE_MS = 2000;

export function TourDriver() {
  const { tourState } = useTourState();
  // The stage machinery (spaces query, DOM scanning, listeners) only exists
  // while a tour is actually running.
  if (tourState?.status !== 'active' || !isTourStage(tourState.stage)) {
    return null;
  }
  return <ActiveTourDriver stage={tourState.stage} />;
}

function ActiveTourDriver({ stage }: { stage: TourStage }) {
  const pathname = usePathname();
  const router = useRouter();
  const { tourState, updateTourState } = useTourState();
  const spacesQuery = useSpaces();

  const stageRef = React.useRef<TourStage | null>(stage);
  stageRef.current = stage;
  const completedStepsRef = React.useRef<readonly string[]>(
    tourState?.completedSteps ?? []
  );
  completedStepsRef.current = tourState?.completedSteps ?? [];

  const advance = React.useCallback(
    (to: TourStage) => {
      const from = stageRef.current;
      updateTourState({
        stage: to,
        completedSteps: mergeCompletedSteps(
          completedStepsRef.current,
          completedStepsForStage(to)
        ),
      });
      track('tour_step_completed', { from_stage: from, to_stage: to });
    },
    [updateTourState]
  );

  const handleSignal = React.useCallback(
    (signal: TourSignal) => {
      const current = stageRef.current;
      if (!current) return;
      const next = transitionTourStage(current, signal);
      if (next) advance(next);
    },
    [advance]
  );

  const skipTour = React.useCallback(() => {
    track('tour_dismissed', { stage: stageRef.current });
    updateTourState({ status: 'dismissed' });
  }, [updateTourState]);

  // Mount reconcile: replay stages advance on a live run-completed event,
  // which a reload mid- or post-replay never re-fires — the persisted stage
  // would sit locked with no coachmark. On mounting into a replay stage, ask
  // the server whether that replay actually finished: completed advances to
  // the stage run-completed would have produced, not-run rewinds to the
  // prompt stage so the user can Send again (the backend resets
  // partially-built docs after failures).
  const mountStageRef = React.useRef(stage);
  const mountReplayId = REPLAY_STAGE_IDS[mountStageRef.current];
  const [reconciled, setReconciled] = React.useState(false);
  const demoStatusQuery = useDemoSpaceStatus({
    enabled: mountReplayId !== undefined && !reconciled,
  });
  React.useEffect(() => {
    if (!mountReplayId || reconciled) return;
    const status = demoStatusQuery.data;
    if (!status) return;
    setReconciled(true);
    // A live event may have moved the tour on while the fetch was in flight;
    // reconciling a stage the tour already left would misfire.
    if (stageRef.current !== mountStageRef.current) return;
    // Mid-run: neither completed nor safe to rewind — the reconnecting
    // realtime stream will deliver the terminal event.
    if (
      !status.completed[mountReplayId] &&
      status.activeReplay === mountReplayId
    ) {
      return;
    }
    handleSignal({
      type: 'replay-reconciled',
      outcome: status.completed[mountReplayId] ? 'completed' : 'not-run',
    });
  }, [demoStatusQuery.data, mountReplayId, reconciled, handleSignal]);

  // The dataRoom beat spotlights the space's subtree — force the node open.
  // The `wanted` flag makes rows that mount during the beat start expanded
  // (navigation remounts the tree); the registry call opens already-mounted
  // rows, retried briefly for slow mounts.
  React.useEffect(() => {
    if (stage !== 'dataRoom') return;
    setTourDemoTreeExpandWanted(true);
    expandTourDemoTree();
    let attempts = 0;
    const interval = window.setInterval(() => {
      attempts += 1;
      expandTourDemoTree();
      if (attempts >= 6) window.clearInterval(interval);
    }, 500);
    return () => {
      window.clearInterval(interval);
      setTourDemoTreeExpandWanted(false);
    };
  }, [stage]);

  // Signal: the demo-space tree node was clicked.
  React.useEffect(() => {
    if (stage !== 'demoSpace') return;
    const onClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('[data-tour="demo-space"]')) {
        // Capture phase runs before the row's own click navigates: the
        // destination tree must mount already expanded (see beat-2 effect).
        setTourDemoTreeExpandWanted(true);
        handleSignal({ type: 'target-clicked' });
      }
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [stage, handleSignal]);

  // The commentsIntro beat spotlights the slide the demo comments landed on:
  // navigate the deck editor there (pins showing), retrying briefly while the
  // editor mounts and registers its navigator.
  React.useEffect(() => {
    if (stage !== 'commentsIntro') return;
    if (tourDeckGoToSlide(COMMENTS_SLIDE_INDEX)) return;
    let attempts = 0;
    const interval = window.setInterval(() => {
      attempts += 1;
      if (tourDeckGoToSlide(COMMENTS_SLIDE_INDEX) || attempts >= 10) {
        window.clearInterval(interval);
      }
    }, 500);
    return () => window.clearInterval(interval);
  }, [stage]);

  // Signal: dictation actually started (voiceIntro's "try it now" path).
  React.useEffect(
    () =>
      subscribeTourDictationEnd(() => {
        handleSignal({ type: 'voice-tried' });
      }),
    [handleSignal]
  );

  // Signal: an editor route opened (sheet/deck created or navigated to), or
  // the user is back on a space's files root (the backToSpace beat).
  React.useEffect(() => {
    if (/^\/sheets\/[^/]+\/edit/.test(pathname)) {
      handleSignal({ type: 'editor-opened', kind: 'sheet' });
    } else if (/^\/decks\/[^/]+\/edit/.test(pathname)) {
      handleSignal({ type: 'editor-opened', kind: 'deck' });
    } else if (/^\/spaces\/[^/]+\/files/.test(pathname)) {
      handleSignal({ type: 'space-files-opened' });
    }
  }, [pathname, handleSignal]);

  // Signals from the chat runtime: sends and run completions.
  React.useEffect(
    () =>
      subscribeTourChatEvents((event) => {
        const current = stageRef.current;
        if (!current) return;
        // Terminal run events must belong to the document this beat targeted
        // — keep-alive editors can complete unrelated runs of the same kind.
        if (
          (event.type === 'run-completed' || event.type === 'run-failed') &&
          getTourExpectedDocumentId() &&
          event.documentId !== getTourExpectedDocumentId()
        ) {
          return;
        }
        if (event.type === 'message-sent' && event.demoReplayId) {
          track('demo_replay_started', { id: event.demoReplayId });
        }
        if (event.type === 'run-completed' || event.type === 'run-failed') {
          const id = REPLAY_STAGE_IDS[current];
          if (id && transitionTourStage(current, { ...event })) {
            track(
              event.type === 'run-completed'
                ? 'demo_replay_completed'
                : 'demo_replay_failed',
              { id }
            );
          }
        }
        handleSignal(event);
      }),
    [handleSignal]
  );

  const config = STAGE_CONFIG[stage];

  // Pending demo-replay id: set while a prompt stage is active, cleared the
  // moment the tour leaves it. Bound to the editor document the stage was
  // entered on so a send from another same-kind editor can't consume it.
  const editorDocumentId =
    pathname.match(/^\/(?:sheets|decks)\/([^/]+)\/edit/)?.[1] ?? null;
  const stageExpectsDocument =
    config?.demoReplayId !== undefined ||
    REPLAY_STAGE_IDS[stage] !== undefined;
  React.useEffect(() => {
    setTourPendingDemoReplayId(
      config?.demoReplayId ?? null,
      stageExpectsDocument ? editorDocumentId : null
    );
    return () => setTourPendingDemoReplayId(null);
  }, [config?.demoReplayId, editorDocumentId, stageExpectsDocument]);

  // Composer lock: held from entering a prompt stage until its replay run
  // completes, so the recorded prompt goes out unedited and the run cannot
  // be stopped mid-replay.
  const composerLocked =
    stage === 'sheetPrompt' ||
    stage === 'sheetReplay' ||
    stage === 'deckPrompt' ||
    stage === 'deckReplay' ||
    stage === 'commentsPrompt' ||
    stage === 'commentsReplay';
  React.useEffect(() => {
    setTourComposerLock(composerLocked);
    return () => setTourComposerLock(false);
  }, [composerLocked]);

  // Canvas lock: the commentsIntro beat shows the slide view-only — the
  // comment pins stay interactive, but the content underneath must not be
  // editable while the coachmark narrates.
  const canvasLocked = stage === 'commentsIntro';
  React.useEffect(() => {
    setTourCanvasLock(canvasLocked);
    return () => setTourCanvasLock(false);
  }, [canvasLocked]);

  // Composer prefill on entering a prompt stage. The target editor may still
  // be mounting, so retry until a visible composer accepts the text. Each
  // attempt first asks the editors to reveal their Chat tab: prefill only
  // targets a visible composer, and the sidebar may have remembered another
  // tab (or never mounted the chat pane at all).
  React.useEffect(() => {
    const prefill = config?.prefill;
    if (!prefill) return;
    let attempts = 0;
    let interval = 0;
    const tryPrefill = () => {
      attempts += 1;
      revealTourChatPanel();
      if (prefillTourComposer(prefill) || attempts >= PREFILL_MAX_ATTEMPTS) {
        window.clearInterval(interval);
      }
    };
    tryPrefill();
    interval = window.setInterval(tryPrefill, 500);
    return () => window.clearInterval(interval);
  }, [config?.prefill]);

  // Record the demo space id once it is known (one attempt per id — a
  // failing PATCH must not retry-loop through the optimistic rollback).
  const demoSpace = spacesQuery.data?.find((s) => s.kind === 'demo');
  const recordedDemoSpaceIdRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!demoSpace || tourState?.demoSpaceId === demoSpace.id) return;
    if (recordedDemoSpaceIdRef.current === demoSpace.id) return;
    recordedDemoSpaceIdRef.current = demoSpace.id;
    updateTourState({ demoSpaceId: demoSpace.id });
  }, [demoSpace, tourState?.demoSpaceId, updateTourState]);

  const anchor = useStageAnchor(config);

  // Fallback grace: on stage entry and page loads the anchor mounts a beat
  // late, and flashing the floating bubble first reads as a glitch. Anchored
  // render is immediate; the fallback waits out a stretch of continuous
  // anchor absence. For beats anchored on the spaces rail, the clock also
  // waits for the spaces query to answer: no rail anchor can exist before
  // then, and on slow loads the bubble floated first and then snapped to
  // the row. Editor-anchored beats don't depend on spaces data and float
  // on the plain grace.
  const holdForSpaces = SPACE_FILES_STAGES.has(stage) && spacesQuery.isPending;
  const [fallbackReady, setFallbackReady] = React.useState(false);
  React.useEffect(() => {
    setFallbackReady(false);
    if (anchor || holdForSpaces) return;
    const timeout = window.setTimeout(
      () => setFallbackReady(true),
      FALLBACK_GRACE_MS
    );
    return () => window.clearTimeout(timeout);
  }, [anchor, stage, holdForSpaces]);

  // While the deck replay streams slides in, follow the filmstrip like a
  // chat log: glide toward the bottom as batches land. The glide survives
  // into deckDone and keeps going until it reaches the bottom once, so a
  // warm replay that lands every batch in the first seconds still gets the
  // full top-to-bottom sweep instead of freezing when the stage flips. A
  // per-frame eased step (instead of scrollTo smooth on an interval) reads
  // as one continuous slow scroll that keeps up as slide batches land.
  // Wheel, touch, or click on the filmstrip hands control to the user for
  // the rest of the beat; a pointer merely resting on it no longer pauses
  // the glide (that read as the scroll dying for no reason).
  const followFilmstrip = stage === 'deckReplay' || stage === 'deckDone';
  React.useEffect(() => {
    if (!followFilmstrip) return;
    let userTookOver = false;
    let observed: HTMLElement | null = null;
    let frame = 0;
    let frameCount = 0;
    let idleUntilFrame = 0;
    let droppedFrames = 0;
    let lastFrameTs = 0;
    let reported = false;
    // One terminal event per follow-session: how the sweep ended and a
    // dropped-frame estimate (inter-frame gap > ~2 vsyncs), so prod tells us
    // whether the glide costs frames on real machines.
    const report = (
      outcome: 'completed' | 'user_took_over' | 'interrupted'
    ) => {
      if (reported) return;
      reported = true;
      track('tour_filmstrip_sweep_ended', {
        outcome,
        total_frames: frameCount,
        dropped_frames: droppedFrames,
      });
    };
    const onUserScroll = () => {
      userTookOver = true;
    };
    const attach = (el: HTMLElement | null) => {
      for (const type of ['wheel', 'touchmove', 'pointerdown'] as const) {
        observed?.removeEventListener(type, onUserScroll);
        el?.addEventListener(type, onUserScroll, { passive: true });
      }
      observed = el;
    };
    const step = (ts: number) => {
      frameCount += 1;
      if (lastFrameTs && ts - lastFrameTs > 34) droppedFrames += 1;
      lastFrameTs = ts;
      // The user owns the strip now — stop burning frames entirely.
      if (userTookOver) {
        report('user_took_over');
        return;
      }
      // Perf: the DOM query (and its geometry-reading visibility check) runs
      // only when the cached node is gone or on a ~500ms cadence, not per
      // frame.
      if (!observed || !observed.isConnected || frameCount % 30 === 0) {
        const el = resolveAnchor(['filmstrip']);
        if (el !== observed) attach(el);
      }
      const el = observed;
      if (el && frameCount >= idleUntilFrame) {
        const remaining = el.scrollHeight - el.clientHeight - el.scrollTop;
        if (remaining >= 1) {
          el.scrollTop += Math.min(14, Math.max(1, remaining * 0.06));
        } else if (stageRef.current === 'deckDone') {
          // Sweep complete and nothing left to stream: stop scheduling
          // frames so the settled list stays under the user's control.
          report('completed');
          return;
        } else {
          // Caught up mid-stream: re-measure every ~10 frames instead of
          // paying the scroll-metric reads (and their potential forced
          // reflow) at full frame rate while waiting for the next batch.
          idleUntilFrame = frameCount + 10;
        }
      }
      frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => {
      cancelAnimationFrame(frame);
      report('interrupted');
      attach(null);
    };
  }, [followFilmstrip]);

  const completeTour = React.useCallback(
    (extraSteps: string[]) => {
      track('tour_completed', {});
      updateTourState({
        status: 'completed',
        completedSteps: mergeCompletedSteps(
          completedStepsRef.current,
          extraSteps
        ),
      });
    },
    [updateTourState]
  );

  if (stage === 'graduation') {
    return (
      <GraduationDialog
        onCreatedSpace={(space) => {
          completeTour(['space']);
          // Same landing as the normal new-space flow: the explorer with the
          // fresh space selected.
          router.push(`/spaces/${space.id}/files`);
        }}
        onKeepExploring={() => completeTour([])}
      />
    );
  }

  const spaceFilesPath = tourState?.demoSpaceId
    ? `/spaces/${tourState.demoSpaceId}/files`
    : null;

  if (!config) return null;
  // The dataRoom beat only renders on the space's files page: beat 1 ends
  // with a navigation there, and anchoring on the OUTGOING page's tree made
  // the coachmark visibly slide when the route swapped underneath it.
  if (
    stage === 'dataRoom' &&
    spaceFilesPath &&
    pathname !== spaceFilesPath
  ) {
    return null;
  }
  // Composer/doc-row beats: no floating fallback while the anchor mounts.
  if (config.requireAnchor && !anchor) return null;
  // Everything else waits out the fallback grace before floating.
  if (!anchor && !fallbackReady) return null;

  // Next always performs the beat's action for the user; the manual
  // interaction (clicking the node, opening the doc, pressing Send) still
  // advances through the same signals.
  const handleNext = () => {
    switch (stage) {
      case 'demoSpace':
        // Set BEFORE navigation: the destination tree can mount before the
        // dataRoom effect runs, and it must mount already expanded.
        setTourDemoTreeExpandWanted(true);
        if (spaceFilesPath && pathname !== spaceFilesPath) {
          router.push(spaceFilesPath);
        }
        handleSignal({ type: 'target-clicked' });
        return;
      case 'dataRoom':
        // Beat 3 anchors on a doc row at the demo space's files root; bring
        // the user back there if they wandered into a subfolder.
        if (spaceFilesPath && pathname !== spaceFilesPath) {
          router.push(spaceFilesPath);
        }
        handleSignal({ type: 'next' });
        return;
      case 'openModel':
      case 'openCim': {
        // The demo doc rows carry their id; the pathname signal advances
        // once the editor route lands. Anchor gone (user wandered off the
        // files page): bring them back to it instead.
        const docId = anchor?.dataset.docId;
        if (docId) {
          router.push(
            stage === 'openModel'
              ? `/sheets/${docId}/edit`
              : `/decks/${docId}/edit`
          );
        } else if (spaceFilesPath) {
          router.push(spaceFilesPath);
        }
        return;
      }
      case 'sheetPrompt':
      case 'deckPrompt':
      case 'commentsPrompt':
        // Sends the prefilled prompt; the message-sent signal advances.
        sendTourComposer();
        return;
      case 'backToSpace':
        if (!spaceFilesPath) return;
        if (pathname.startsWith(spaceFilesPath)) {
          handleSignal({ type: 'space-files-opened' });
        } else {
          router.push(spaceFilesPath);
        }
        return;
      default:
        handleSignal({ type: 'next' });
    }
  };

  // Back is a plain stage rewind: no completion bookkeeping, no tracking.
  const backTarget = BACK_TARGETS[stage];
  const handleBack = backTarget
    ? () => {
        if (
          SPACE_FILES_STAGES.has(backTarget) &&
          spaceFilesPath &&
          pathname !== spaceFilesPath
        ) {
          router.push(spaceFilesPath);
        }
        updateTourState({ stage: backTarget });
      }
    : undefined;

  // voiceIntro names the dictation key: Option on macOS, Alt everywhere else.
  const copy =
    stage === 'voiceIntro'
      ? {
          ...config.copy,
          body: config.copy.body.replace(
            '{key}',
            isMacPlatform() ? 'Option' : 'Alt'
          ),
        }
      : config.copy;

  return (
    <Coachmark
      key={stage}
      copy={copy}
      anchor={anchor}
      side={config.side}
      align={config.align}
      noScrim={config.scrim === false}
      interactive={config.interactive !== false}
      onNext={handleNext}
      onBack={handleBack}
      onSkip={skipTour}
    />
  );
}

// Theme-aware crow mark; mounted-gated so SSR and the client first paint agree.
function GraduationMark() {
  const { resolvedTheme } = useTheme();
  const [hasMounted, setHasMounted] = React.useState(false);
  React.useEffect(() => setHasMounted(true), []);
  const mark =
    hasMounted && resolvedTheme === 'light'
      ? '/pc-icon-black.png'
      : '/pc-icon.png';
  return (
    <Image
      src={mark}
      alt="Pitchcrow"
      width={80}
      height={80}
      className="mx-auto size-9"
    />
  );
}

function GraduationDialog({
  onCreatedSpace,
  onKeepExploring,
}: {
  onCreatedSpace: (space: Space) => void;
  onKeepExploring: () => void;
}) {
  const [showNewSpace, setShowNewSpace] = React.useState(false);

  return (
    <>
      <Dialog
        open={!showNewSpace}
        onOpenChange={(open) => {
          // Closing the celebration any other way counts as done too.
          if (!open) onKeepExploring();
        }}
      >
        <DialogContent
          className="max-w-sm p-6 text-center"
          showCloseButton={false}
        >
          <GraduationMark />
          <DialogTitle className="mt-4 text-lg font-semibold text-fg">
            {GRADUATION.title}
          </DialogTitle>
          <DialogDescription className="mt-2 text-base leading-relaxed text-fg-secondary">
            {GRADUATION.body}
          </DialogDescription>
          <div className="mt-5 flex flex-col gap-2">
            <Button
              size="md"
              className="w-full"
              onClick={() => setShowNewSpace(true)}
            >
              <IconPlus className="size-4" /> {GRADUATION.primaryCta}
            </Button>
            <Button
              variant="tertiary"
              size="md"
              className="w-full"
              onClick={onKeepExploring}
            >
              {GRADUATION.secondaryCta}
            </Button>
          </div>
          <p className="mt-4 text-sm text-fg-light">{GRADUATION.note}</p>
        </DialogContent>
      </Dialog>
      <NewSpaceDialog
        open={showNewSpace}
        onOpenChange={setShowNewSpace}
        onCreated={onCreatedSpace}
      />
    </>
  );
}
